export type LoopCallbacks = {
  update: (fixedDeltaSeconds: number) => void;
  render: (interpolation: number) => void;
};

export type AnimationFrameDriver = {
  request: (callback: FrameRequestCallback) => number;
  cancel: (handle: number) => void;
  now: () => number;
};

function createBrowserDriver(): AnimationFrameDriver {
  return {
    request: window.requestAnimationFrame.bind(window),
    cancel: window.cancelAnimationFrame.bind(window),
    now: () => performance.now(),
  };
}

/**
 * Simulates at a stable cadence while rendering at the display's cadence.
 * The maximum frame duration prevents a backgrounded tab from triggering a
 * huge simulation catch-up when it becomes visible again.
 */
export class FixedStepLoop {
  private accumulator = 0;
  private frameHandle: number | undefined;
  private lastTimestamp = 0;
  private running = false;
  private readonly driver: AnimationFrameDriver;

  public constructor(
    private readonly callbacks: LoopCallbacks,
    private readonly stepSeconds = 1 / 60,
    private readonly maxFrameSeconds = 0.25,
    driver?: AnimationFrameDriver,
  ) {
    this.driver = driver ?? createBrowserDriver();
  }

  public start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTimestamp = this.driver.now();
    this.frameHandle = this.driver.request(this.onFrame);
  }

  public stop(): void {
    this.running = false;
    if (this.frameHandle !== undefined) this.driver.cancel(this.frameHandle);
    this.frameHandle = undefined;
    this.accumulator = 0;
  }

  public advance(frameSeconds: number): void {
    const elapsed = Math.min(Math.max(frameSeconds, 0), this.maxFrameSeconds);
    this.accumulator += elapsed;

    while (this.accumulator >= this.stepSeconds) {
      this.callbacks.update(this.stepSeconds);
      this.accumulator -= this.stepSeconds;
    }

    this.callbacks.render(this.accumulator / this.stepSeconds);
  }

  private readonly onFrame = (timestamp: number): void => {
    if (!this.running) return;
    this.advance((timestamp - this.lastTimestamp) / 1_000);
    this.lastTimestamp = timestamp;
    this.frameHandle = this.driver.request(this.onFrame);
  };
}
